import { ILibraryDocumentListItem } from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";

interface DeleteDocumentDialogProps {
    open: boolean;
    onClose: () => void;
    document: ILibraryDocumentListItem;
    onConfirm: () => void;
    isDeleting: boolean;
}

/**
 * Parent-owned-mutation contract: the menu owns the deleteDocument
 * useMutation; this dialog only receives `onConfirm` + `isDeleting`. Click
 * Delete → calls `onConfirm` (does NOT auto-close; parent decides).
 *
 * Defensive close: Variant B (mid-operation-aware on isDeleting).
 */
export const DeleteDocumentDialog = ({
    open,
    onClose,
    document,
    onConfirm,
    isDeleting,
}: DeleteDocumentDialogProps) => {
    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v && !isDeleting) onClose();
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete Document</DialogTitle>
                </DialogHeader>
                <div
                    data-slot="alert"
                    className="border-destructive/15 bg-destructive/5 flex items-start gap-3 rounded-md border p-3 text-sm"
                >
                    <AlertTriangle className="text-destructive size-4 shrink-0" />
                    <p className="text-foreground">
                        This action cannot be undone. The document will be
                        permanently removed.
                    </p>
                </div>
                <div className="space-y-2 text-sm">
                    <p>Are you sure you want to delete this document?</p>
                    <p className="break-all">
                        <strong>{document.document_name}</strong>
                    </p>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isDeleting}
                        aria-label="Delete"
                    >
                        {isDeleting ? (
                            <>
                                <Loader2 className="mr-2 size-4 animate-spin" />
                                Deleting…
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
